import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Stack,
    Alert,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Security as SecurityIcon,
    Check as CheckIcon,
    Draw as DrawIcon
} from '@mui/icons-material';
import SignatureModal from '../SignatureModal';
import { initializeDB } from '../../../config/db';

export default function SafetyAcknowledgmentModal({
    open,
    onClose,
    employee,
    existingAcknowledgment,
    hazardsData, // from stepThree.energyExposures
    onAcknowledge
}) {
    const [acknowledged, setAcknowledged] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [energyMap, setEnergyMap] = useState(new Map());

    // Load energy data from IndexedDB
    React.useEffect(() => {
        if (!open) return;

        const loadEnergies = async () => {
            try {
                const db = await initializeDB();
                const tx = db.transaction('energy', 'readonly');
                const store = tx.objectStore('energy');
                const energies = await store.getAll();

                const map = new Map();
                energies.forEach(energy => {
                    if (Array.isArray(energy.hazards)) {
                        energy.hazards.forEach(hazard => {
                            const hazardKey = hazard.value || hazard.name || hazard.id;
                            if (hazardKey) {
                                map.set(hazardKey, hazard.preventHazards || []);
                            }
                        });
                    }
                });
                setEnergyMap(map);
            } catch (error) {
                console.error('Failed to load energy data:', error);
            }
        };

        loadEnergies();
    }, [open]);

    // Set acknowledged state when modal opens or when existingAcknowledgment changes
    React.useEffect(() => {
        if (open && existingAcknowledgment?.acknowledged) {
            setAcknowledged(true);
        } else if (!open) {
            setAcknowledged(false);
        }
    }, [open, existingAcknowledgment]);

    if (!employee) return null;

    const handleAcknowledgeChange = (event) => {
        const isChecked = event.target.checked;
        setAcknowledged(isChecked);

        if (isChecked && !existingAcknowledgment?.signature) {
            // Don't save acknowledgment yet - wait for signature
            // Open signature modal on top of this modal
            setSignatureModalOpen(true);
        }
    };

    const handleRemoveSignature = () => {
        // Remove signature but keep acknowledgment
        onAcknowledge({
            employeeName: employee.FullName,
            employeeId: employee.EmployeeId || null,
            tempId: employee.TempId || null,
            _type: employee._type,
            acknowledged: true,
            acknowledgedAt: existingAcknowledgment?.acknowledgedAt || new Date().toISOString()
        });
    };

    const handleSignClick = () => {
        if (!acknowledged) return;
        setSignatureModalOpen(true);
    };

    const handleSignatureSave = (signatureData) => {
        onAcknowledge({
            employeeName: employee.FullName,
            employeeId: employee.EmployeeId || null,
            tempId: employee.TempId || null,
            _type: employee._type,
            acknowledged: true,
            acknowledgedAt: new Date().toISOString(),
            signature: signatureData,
            signedAt: new Date().toISOString()
        });
        setSignatureModalOpen(false);
        setAcknowledged(false);
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ py: 2, px: 3, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <SecurityIcon sx={{ fontSize: 28, color: '#1976d2' }} />
                        <Box>
                            <Typography variant="h6" fontWeight={600}>Safety Acknowledgment</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {employee.FullName}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2, maxHeight: '60vh', overflow: 'auto' }}>
                    {(() => {
                        // Extract all hazards from all exposures
                        // Handle both energyExposures structure and energyActivityRows structure
                        const allHazards = hazardsData.flatMap((row, rowIndex) => {
                            let hazardsList = [];
                            let workActivity = row.workActivity || '';

                            // PRIORITY: Check energySelections first (new structure with multiple energies)
                            if (row.energySelections && Array.isArray(row.energySelections)) {
                                hazardsList = row.energySelections.flatMap(selection => selection.hazards || []);
                            }
                            // FALLBACK: If no energySelections, use hazards directly on row (legacy structure)
                            else if (row.hazards && Array.isArray(row.hazards)) {
                                hazardsList = row.hazards;
                            }

                            return hazardsList
                                .filter(hazard => {
                                    const hazardValue = hazard?.value || hazard?.name || hazard?.hazard || '';
                                    return hazardValue && hazardValue.trim();
                                })
                                .map((hazard, hazardIndex) => {
                                    const hazardValue = hazard.value || hazard.name || hazard.hazard || '';
                                    // PRIORITY: Use hazard's own preventHazards first (selected by user in HazardActionsModal)
                                    // FALLBACK: energyMap (default from IndexedDB) only if no custom selection
                                    const hasCustomPreventHazards = hazard.preventHazards && Array.isArray(hazard.preventHazards) && hazard.preventHazards.length > 0;
                                    const preventHazards = hasCustomPreventHazards
                                        ? hazard.preventHazards
                                        : (energyMap.get(hazardValue) || hazard.controls || hazard.controlMeasures || []);
                                    return {
                                        key: `${rowIndex}-${hazardIndex}`,
                                        value: hazardValue,
                                        preventHazards,
                                        workActivity
                                    };
                                });
                        });

                        if (allHazards.length === 0) {
                            return <Alert severity="warning">No hazards recorded</Alert>;
                        }

                        return (
                            <Stack spacing={0.75}>
                                {allHazards.map((hazard) => (
                                    <Box
                                        key={hazard.key}
                                        sx={{
                                            p: 1,
                                            border: '1px solid #e0e0e0',
                                            borderLeft: '3px solid #f57c00',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <WarningIcon sx={{ fontSize: 16, color: '#f57c00', mt: 0.1, flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {hazard.workActivity && (
                                                    <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600, display: 'block', mb: 0.25 }}>
                                                        {hazard.workActivity}
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                                                    {hazard.value}
                                                </Typography>
                                                {hazard.preventHazards.length > 0 ? (
                                                    <Stack spacing={0.125}>
                                                        {hazard.preventHazards.map((measure, mIdx) => {
                                                            const measureText = typeof measure === 'string' ? measure : (measure?.value || measure?.name || '');
                                                            return (
                                                                <Typography key={mIdx} variant="caption" sx={{ pl: 1, position: 'relative', color: '#666' }}>
                                                                    <span style={{ position: 'absolute', left: 0 }}>•</span>
                                                                    {measureText}
                                                                </Typography>
                                                            );
                                                        })}
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                                                        To be determined
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        );
                    })()}
                </DialogContent>

                <Divider />

                <Box sx={{ px: 3, py: 2, backgroundColor: '#f8f9fa' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={acknowledged}
                                onChange={handleAcknowledgeChange}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body1" fontWeight={500}>
                                I have read and understood all hazards and control measures
                            </Typography>
                        }
                    />
                    {existingAcknowledgment?.signature && (
                        <Box sx={{ mt: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: 'white' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Current Signature:
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 200,
                                    height: 60,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                }}
                            >
                                <img src={existingAcknowledgment.signature} alt="signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </Box>
                            {existingAcknowledgment?.signedAt && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Signed: {new Date(existingAcknowledgment.signedAt).toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Divider />

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={onClose}>
                        Close
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {existingAcknowledgment?.signature && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemoveSignature}
                            size="small"
                        >
                            Remove Signature
                        </Button>
                    )}
                    {acknowledged && (
                        <Button
                            variant="contained"
                            startIcon={<DrawIcon />}
                            onClick={handleSignClick}
                        >
                            {existingAcknowledgment?.signature ? 'Change Signature' : 'Sign'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <SignatureModal
                open={signatureModalOpen}
                onClose={() => {
                    setSignatureModalOpen(false);
                    // Reset acknowledged state if no signature was saved
                    if (!existingAcknowledgment?.signature) {
                        setAcknowledged(false);
                    }
                }}
                existingSignature={existingAcknowledgment?.signature || ''}
                employeeName={employee.FullName}
                onSave={handleSignatureSave}
            />
        </>
    );
}
